import { useMemo, useState } from "react";
import JSZip from "jszip";
import { Code2, Eye, Download, FileCode2, RefreshCw, ShieldCheck, ShieldAlert, ChevronDown, Check, X, MousePointerClick, Terminal, Bug, Wrench } from "lucide-react";
import { CodeBlock } from "./CodeBlock";

/* Inline referenced .css/.js into the root HTML so a plain iframe can render
   a static (STACK=static) project without a server. */
function assemblePreview(files) {
  const html = files.find((f) => /\.html?$/i.test(f.path));
  if (!html) return null;
  let out = html.content;
  files
    .filter((f) => /\.css$/i.test(f.path))
    .forEach((css) => {
      const name = css.path.split("/").pop();
      const re = new RegExp(`<link[^>]*href=["'][^"']*${name}["'][^>]*>`, "i");
      if (re.test(out)) out = out.replace(re, `<style>\n${css.content}\n</style>`);
    });
  files
    .filter((f) => /\.js$/i.test(f.path) && !/\.jsx$/i.test(f.path))
    .forEach((js) => {
      const name = js.path.split("/").pop();
      const re = new RegExp(`<script[^>]*src=["'][^"']*${name}["'][^>]*>\\s*</script>`, "i");
      if (re.test(out)) out = out.replace(re, `<script>\n${js.content}\n</script>`);
    });
  return out;
}

const slugify = (s) =>
  (s || "projet")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "projet";

function download(name, content, type = "text/plain;charset=utf-8") {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function ReportRow({ ok, icon: Icon, label, value, neutral = false }) {
  const tone = neutral ? "text-slate-400" : ok ? "text-emerald-300" : "text-amber-300";
  const iconTone = neutral ? "text-slate-500" : ok ? "text-emerald-400" : "text-amber-400";
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${iconTone}`} />
      <span className="text-slate-300">{label}</span>
      <span className={`ml-auto font-mono-hud text-[10px] ${tone}`}>{value}</span>
    </div>
  );
}

export function BuilderResult({ project, compact = false }) {
  const files = project?.files || [];
  const [active, setActive] = useState(0);
  const preview = useMemo(() => (project?.previewable ? assemblePreview(files) : null), [project]); // eslint-disable-line
  const [view, setView] = useState(preview ? "preview" : "code");
  const v = project?.verification;
  const [showReport, setShowReport] = useState(v ? !v.ok : false);

  if (!files.length) return null;
  const activeFile = files[Math.min(active, files.length - 1)];

  const doDownload = async () => {
    if (files.length === 1) {
      download(files[0].path.split("/").pop() || "index.html", files[0].content);
      return;
    }
    const zip = new JSZip();
    files.forEach((f) => zip.file(f.path, f.content));
    const blob = await zip.generateAsync({ type: "blob" });
    download(`${slugify(project.title)}.zip`, blob, "application/zip");
  };

  return (
    <div
      data-testid="builder-result"
      className="overflow-hidden rounded-xl border border-[var(--border-hud)] bg-[rgba(3,8,18,0.75)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-hud)] bg-[rgba(255,255,255,0.03)] px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <FileCode2 className="h-4 w-4 shrink-0 accent-text" />
          <span className="truncate font-mono-hud text-[11px] tracking-wide text-white">
            {project.title || "Projet"}
          </span>
          <span className="shrink-0 rounded-full border border-white/10 px-1.5 py-0.5 font-mono-hud text-[9px] uppercase tracking-wider text-slate-400">
            {project.stack || "static"} · {files.length} fichier{files.length > 1 ? "s" : ""}
          </span>
          {project.verification?.checked && (
            project.verification.ok ? (
              <span
                data-testid="builder-verification-ok"
                title={`Vérifié automatiquement : 0 erreur console, boutons fonctionnels${project.verification.repair_rounds ? ` (${project.verification.repair_rounds} correction auto)` : ""}`}
                className="flex shrink-0 items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-1.5 py-0.5 font-mono-hud text-[9px] uppercase tracking-wider text-emerald-300"
              >
                <ShieldCheck className="h-3 w-3" /> Vérifié · 0 erreur
              </span>
            ) : (
              <span
                data-testid="builder-verification-warn"
                title={(project.verification.issues || []).join(" · ")}
                className="flex shrink-0 items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 font-mono-hud text-[9px] uppercase tracking-wider text-amber-300"
              >
                <ShieldAlert className="h-3 w-3" /> {(project.verification.issues || []).length} point(s) à vérifier
              </span>
            )
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex overflow-hidden rounded-md border border-white/10">
            <button
              type="button"
              data-testid="builder-view-preview"
              disabled={!preview}
              onClick={() => setView("preview")}
              className={`flex items-center gap-1 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-wider transition-colors ${
                view === "preview" ? "bg-[var(--accent-a25)] text-white" : "text-slate-400 hover:text-white"
              } disabled:opacity-40`}
            >
              <Eye className="h-3.5 w-3.5" /> Aperçu
            </button>
            <button
              type="button"
              data-testid="builder-view-code"
              onClick={() => setView("code")}
              className={`flex items-center gap-1 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-wider transition-colors ${
                view === "code" ? "bg-[var(--accent-a25)] text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              <Code2 className="h-3.5 w-3.5" /> Code
            </button>
          </div>
          <button
            type="button"
            data-testid="builder-download"
            onClick={doDownload}
            className="flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-wider text-slate-200 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Download className="h-3.5 w-3.5" /> {files.length === 1 ? "Fichier" : "Zip"}
          </button>
        </div>
      </div>

      {v?.checked && (
        <div className="border-b border-[var(--border-hud)] bg-[rgba(255,255,255,0.02)]">
          <button
            type="button"
            data-testid="builder-verification-toggle"
            onClick={() => setShowReport((s) => !s)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-white/5"
          >
            {v.ok ? (
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
            ) : (
              <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-400" />
            )}
            <span className="font-mono-hud text-[10px] uppercase tracking-wider text-slate-200">
              Rapport de vérification {v.ok ? "· 0 bug garanti" : `· ${(v.issues || []).length} point(s) à vérifier`}
            </span>
            <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${showReport ? "rotate-180" : ""}`} />
          </button>
          {showReport && (
            <div data-testid="builder-verification-report" className="space-y-1.5 px-3 pb-3 pt-0.5">
              <ReportRow
                ok={(v.console_errors || []).length === 0}
                icon={Terminal}
                label="Erreurs dans la console (chargement + clic)"
                value={(v.console_errors || []).length === 0 ? "Aucune" : `${v.console_errors.length} détectée(s)`}
              />
              <ReportRow
                ok={(v.page_errors || []).length === 0}
                icon={Bug}
                label="Erreurs JavaScript non interceptées"
                value={(v.page_errors || []).length === 0 ? "Aucune" : `${v.page_errors.length} détectée(s)`}
              />
              <ReportRow
                ok
                icon={MousePointerClick}
                label="Boutons interactifs testés (clic réel)"
                value={v.available ? `${v.clicked || 0}/${v.buttons || 0} cliqués` : "Audit statique seul"}
                neutral={!v.available}
              />
              <ReportRow
                ok
                icon={Wrench}
                label="Corrections automatiques appliquées"
                value={`${v.repair_rounds || 0} tour(s)`}
                neutral={!v.repair_rounds}
              />
              <ReportRow
                ok={v.available}
                icon={ShieldCheck}
                label="Audit navigateur réel (Chrome headless)"
                value={v.available ? "Effectué" : "Indisponible"}
                neutral={!v.available}
              />
              {(v.issues || []).length > 0 && (
                <div className="mt-2 rounded-md border border-amber-400/30 bg-amber-400/5 p-2">
                  <p className="mb-1 font-mono-hud text-[9px] uppercase tracking-wider text-amber-300">Points à vérifier</p>
                  <ul className="space-y-0.5">
                    {(v.issues || []).slice(0, 8).map((it, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-100/80">
                        <X className="mt-0.5 h-3 w-3 shrink-0 text-amber-400" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {v.ok && (
                <p className="mt-1.5 flex items-center gap-1.5 font-mono-hud text-[10px] text-emerald-300">
                  <Check className="h-3.5 w-3.5" /> Projet vérifié automatiquement — prêt à l'emploi.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {view === "preview" && preview ? (
        <div className="relative bg-white">
          <iframe
            key={preview.length}
            data-testid="builder-preview-iframe"
            title="Aperçu du projet"
            srcDoc={preview}
            sandbox="allow-scripts allow-modals allow-popups allow-forms"
            className={`w-full ${compact ? "h-[300px]" : "h-[540px]"}`}
          />
        </div>
      ) : (
        <div>
          {files.length > 1 && (
            <div className="flex gap-1 overflow-x-auto border-b border-[var(--border-hud)] bg-[rgba(255,255,255,0.02)] px-2 py-1.5 no-scrollbar">
              {files.map((f, i) => (
                <button
                  key={f.path}
                  type="button"
                  data-testid={`builder-file-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`shrink-0 rounded-md px-2.5 py-1 font-mono text-[11px] transition-colors ${
                    i === active ? "bg-[var(--accent-a20)] text-white" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {f.path.split("/").pop()}
                </button>
              ))}
            </div>
          )}
          <div className="p-2 sm:p-3">
            <CodeBlock code={activeFile.content} language={activeFile.language} filename={activeFile.path} />
          </div>
        </div>
      )}
      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
}
